/** @file poseEdit: one gizmo or IK gesture on the selected joints, from pointer-down to its commit. */
import type { Command } from '@zilo/pose3d-commands/types/command';
import type { Clip, NodeKey } from '@zilo/pose3d-format/types/clip';
import type { Quat } from '@zilo/pose3d-math/quat/quat';
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import type { TransformSpace } from '../../state/editorTypes';
import { applyDragToKey } from './applyDragToKey';
import { dragReadout } from './dragReadout';
import { dragSession } from './dragSession';
import type { GizmoModel } from './gizmoModel';
import type { GizmoHandle } from './gizmoShape';
import { ikDrag } from './ikDrag';
import { keyFrameDrag } from './keyFrameDrag';
import { setPoseCommand } from './setPoseCommand';
import { startKeyOf } from './startKeyOf';
import type { WorldDrag } from './viewportDrag';

/** A joint the gesture edits: its name, the rotation its key is expressed in. */
export type EditedNode = { name: string; parentQ: Quat };

/** The gesture: tool, handle (3 = centre, 4..6 planes; ignored by IK), gizmo, view, pointer, joints, clip, frame. */
export type PoseEditStart = {
  tool: 'rotate' | 'move' | 'scale' | 'ik'; axis: GizmoHandle; model: GizmoModel; view: View; pointer: [number, number];
  nodes: EditedNode[]; space: TransformSpace; clip: Clip; frame: number;
};

/**
 * `keys(pointer, snapDeg)` = the edited joints' keys now (for `setPoseOverride`); `command(keys)` = the commit;
 * `lastDrag()` = the world gesture the last `keys` call measured (null before the first); `readout(keys, snapDeg)` =
 * the active (last) joint's stored value and change (`dragReadout`).
 */
export type PoseEdit = {
  keys(pointer: [number, number], snapDeg: number): Record<string, NodeKey>; command(keys: Record<string, NodeKey>): Command; lastDrag(): WorldDrag | null;
  readout(keys: Record<string, NodeKey>, snapDeg: number): string | null;
};

/**
 * Every edited joint starts from `startKeyOf` (its key at the frame, else the interpolated one); a pointer position
 * becomes a world gesture (`dragSession`, or `ikDrag` for the IK tool, which keys the dragged joint's `pos` only),
 * then each joint's key frame (`keyFrameDrag`) and its new key (`applyDragToKey`).
 */
export function poseEdit(start: PoseEditStart): PoseEdit {
  const starts = new Map(start.nodes.map((n) => [n.name, startKeyOf(start.clip, n.name, start.frame)] as const));
  const handle = start.axis > 2 ? null : (start.axis as 0 | 1 | 2);
  const measure: (p: [number, number]) => WorldDrag = start.tool === 'ik'
    ? (p) => ikDrag(start.view, start.pointer, p)
    : dragSession({ kind: start.tool, axis: start.axis, model: start.model, view: start.view, pointer: start.pointer }).update;
  let last: WorldDrag | null = null;
  return {
    keys: (pointer, snapDeg) => {
      const drag = measure(pointer);
      last = drag;
      return Object.fromEntries(start.nodes.map((n) => {
        const key = applyDragToKey(starts.get(n.name) ?? {}, keyFrameDrag(drag, start.space, handle, n.parentQ), start.space, snapDeg);
        return [n.name, key];
      }));
    },
    command: (keys) => setPoseCommand(start.clip, start.frame, keys),
    lastDrag: () => last,
    readout: (keys, snapDeg) => {
      const name = start.nodes.at(-1)?.name;
      const key = name === undefined ? undefined : keys[name];
      if (name === undefined || key === undefined) return null;
      return dragReadout(start.tool === 'ik' ? 'move' : start.tool, start.axis, starts.get(name) ?? {}, key, snapDeg);
    },
  };
}
